import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save, RotateCcw } from "lucide-react";
import { DEFAULT_THEME, FONT_CHOICES } from "@/context/SiteContext";

const COLOR_FIELDS = [
  { key: "background", label: "Fundal principal", help: "Fundalul dominant al site-ului (recomandat: alb sau foarte deschis)" },
  { key: "foreground", label: "Text principal", help: "Culoarea textului și a butoanelor primary (recomandat: negru sau foarte închis)" },
  { key: "muted", label: "Fundal blockquote", help: "Fundalul citatelor și zonelor subtile (recomandat: gri foarte deschis)" },
  { key: "border", label: "Separatori subtili", help: "Bordurile discrete între elemente" },
  { key: "grey", label: "Text secundar", help: "Text de acompaniere și metadate" },
];

export default function ThemeEditor({ onSaved }) {
  const [colors, setColors] = useState(DEFAULT_THEME.colors);
  const [fonts, setFonts] = useState(DEFAULT_THEME.fonts);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/site/theme");
        setColors({ ...DEFAULT_THEME.colors, ...(data.colors || {}) });
        setFonts({ ...DEFAULT_THEME.fonts, ...(data.fonts || {}) });
      } catch { /* ignore */ }
    })();
  }, []);

  const setColor = (k, v) => { setColors((c) => ({ ...c, [k]: v })); setDirty(true); };
  const setFont = (k, v) => { setFonts((f) => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site/theme", { colors, fonts });
      toast.success("Temă salvată — reîncarcă pagina pentru a vedea efectul complet");
      setDirty(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const resetAll = () => {
    setColors(DEFAULT_THEME.colors);
    setFonts(DEFAULT_THEME.fonts);
    setDirty(true);
  };

  return (
    <div data-testid="theme-editor">
      <div className="sticky top-20 z-20 bg-white border border-black p-4 mb-8 flex items-center justify-between">
        <div>
          <div className="font-mono-label text-black/60">Editor temă</div>
          <div className="mt-1 text-sm">
            {dirty ? <span><strong>Modificări nesalvate</strong></span> : <span className="text-black/50">Salvată</span>}
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={resetAll} className="font-mono-label border border-black px-4 py-3 hover:bg-black hover:text-white inline-flex items-center gap-2">
            <RotateCcw size={14} /> Resetează
          </button>
          <button onClick={save} disabled={saving || !dirty} data-testid="save-theme-btn"
            className="font-mono-label bg-black text-white px-6 py-3 border border-black hover:bg-white hover:text-black disabled:opacity-40 inline-flex items-center gap-2">
            <Save size={14} /> {saving ? "Se salvează..." : "Salvează tema"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl">
        {/* Colors */}
        <div>
          <div className="font-mono-label text-black/60 mb-4">Culori globale</div>
          <div className="border border-black divide-y divide-black">
            {COLOR_FIELDS.map((f) => (
              <div key={f.key} className="p-5 flex items-start gap-5">
                <div className="w-16 h-16 border border-black shrink-0" style={{ background: colors[f.key] || "#000" }} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold uppercase text-sm">{f.label}</div>
                  <div className="text-xs text-black/60 mt-1">{f.help}</div>
                  <div className="mt-3 flex items-center gap-3">
                    <input type="color" className="w-12 h-10 border border-black cursor-pointer p-0"
                      data-testid={`color-picker-${f.key}`}
                      value={colors[f.key] || "#000000"}
                      onChange={(e) => setColor(f.key, e.target.value)} />
                    <input type="text" className="flex-1 border border-black px-3 py-2 font-mono text-sm"
                      data-testid={`color-input-${f.key}`}
                      value={colors[f.key] || ""}
                      onChange={(e) => setColor(f.key, e.target.value)}
                      placeholder="#000000" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Fonts */}
        <div>
          <div className="font-mono-label text-black/60 mb-4">Fonturi globale</div>
          <div className="border border-black divide-y divide-black">
            {[
              { key: "heading", label: "Titluri (H1–H4)", choices: FONT_CHOICES.heading },
              { key: "body", label: "Text (paragrafe, meniuri)", choices: FONT_CHOICES.body },
              { key: "mono", label: "Etichete monospace", choices: FONT_CHOICES.mono },
            ].map((f) => (
              <div key={f.key} className="p-5">
                <div className="font-display font-bold uppercase text-sm">{f.label}</div>
                <select className="w-full mt-3 border border-black px-4 py-3 bg-white"
                  data-testid={`font-select-${f.key}`}
                  value={fonts[f.key] || ""}
                  onChange={(e) => setFont(f.key, e.target.value)}>
                  {f.choices.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <div className="mt-4 p-4 border border-black/20 bg-neutral-50" style={{ fontFamily: `'${fonts[f.key]}', sans-serif` }}>
                  <div className="text-2xl" style={{ fontFamily: `'${fonts[f.key]}', sans-serif`, fontWeight: f.key === "heading" ? 900 : 400 }}>
                    {f.key === "heading" ? "SUNTEC GROUP" : f.key === "body" ? "Soluții integrate pentru achiziții." : "01 / DEPARTAMENTE"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
